import { FormEvent, useEffect, useMemo, useState } from 'react';
import { authzApi, type Permission } from '../../api/authz';

export function PermissionsPage() {
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [search, setSearch] = useState('');
  const [resource, setResource] = useState('');
  const [action, setAction] = useState('');
  const [description, setDescription] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    void authzApi.listPermissions().then(setPermissions).catch((err) => setError(String(err)));
  }, []);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return permissions.filter((p) =>
      p.key.toLowerCase().includes(q) || p.resource.toLowerCase().includes(q) || p.description.toLowerCase().includes(q),
    );
  }, [permissions, search]);

  const groups = useMemo(() => {
    const map = new Map<string, Permission[]>();
    for (const p of filtered) {
      const g = p.group || 'other';
      map.set(g, [...(map.get(g) ?? []), p]);
    }
    return [...map.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [filtered]);

  async function onCreate(e: FormEvent) {
    e.preventDefault();
    try {
      await authzApi.createPermission({ resource, action, description });
      setPermissions(await authzApi.listPermissions());
      setResource('');
      setAction('');
      setDescription('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Create failed');
    }
  }

  return (
    <div className="mx-auto max-w-6xl p-6">
      <h1 className="text-2xl font-semibold text-slate-900">Permissions</h1>
      <p className="mt-1 text-sm text-slate-600">Platform permission catalogue and custom permissions.</p>
      {error && <p className="mt-4 rounded-lg bg-red-50 p-3 text-sm text-red-700">{error}</p>}

      <div className="mt-4 flex flex-wrap gap-3">
        <input placeholder="Search permissions…" value={search} onChange={(e) => setSearch(e.target.value)} className="min-w-[240px] flex-1 rounded-lg border border-slate-300 px-3 py-2" />
      </div>

      <form onSubmit={onCreate} className="mt-4 grid gap-3 rounded-xl border border-slate-200 bg-white p-4 md:grid-cols-4">
        <input required placeholder="Resource" value={resource} onChange={(e) => setResource(e.target.value)} className="rounded-lg border border-slate-300 px-3 py-2" />
        <input required placeholder="Action" value={action} onChange={(e) => setAction(e.target.value)} className="rounded-lg border border-slate-300 px-3 py-2" />
        <input placeholder="Description" value={description} onChange={(e) => setDescription(e.target.value)} className="rounded-lg border border-slate-300 px-3 py-2 md:col-span-2" />
        <button type="submit" className="rounded-lg bg-slate-900 px-4 py-2 text-white md:col-span-4 md:w-fit">Add permission</button>
      </form>

      <div className="mt-6 space-y-6">
        {groups.map(([group, items]) => (
          <section key={group}>
            <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-slate-500">{group}</h2>
            <ul className="grid gap-2 md:grid-cols-2">
              {items.map((p) => (
                <li key={p.key} className="rounded-lg border border-slate-200 bg-white p-3 text-sm">
                  <code className="font-medium text-slate-900">{p.key}</code>
                  <p className="mt-1 text-slate-600">{p.description}</p>
                </li>
              ))}
            </ul>
          </section>
        ))}
      </div>
    </div>
  );
}
